import { MaterialIcon } from '@gorila-shared-ui/components';
import { Input } from 'baseui/input';
import { useEffect, useState } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { JOURNEY_END_POINT, JOURNEY_START_POINT } from '../../constants/journeys';
import { environment } from '../../environments/environment';
import { useCamera } from '../../hooks/useCamera';
import { useStyles } from '../../hooks/useStyles';
import { editingJourneyState, journeySelectPointState } from '../../storage/journeys';
import { GeoPlace } from '../../types/geoModels';

export function MapSearchBox() {
  const { theme, css } = useStyles();
  const { zoomTo } = useCamera();
  const journeySelectPoint = useRecoilValue(journeySelectPointState);
  const resetJourneySelectPoint = useResetRecoilState(journeySelectPointState);
  const [editingJourney, setEditingJourney] = useRecoilState(editingJourneyState);
  const [selectedPlace, setSelectedPlace] = useState<GeoPlace>();
  const [isFocused, setIsFocused] = useState(false);
  const { ref } = usePlacesWidget<HTMLInputElement>({
    apiKey: environment.googleMapsAPIKey,
    inputAutocompleteValue: '',
    options: {
      types: [],
      fields: ['formatted_address', 'geometry'],
    },
    onPlaceSelected: (place) => {
      const point = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
      const address = place.formatted_address;
      setSelectedPlace({ point, address });
    },
  });

  useEffect(() => {
    if (ref.current) {
      ref.current.onblur = () => {
        setIsFocused(false);
      };
      ref.current.onfocus = () => {
        setIsFocused(true);
      };
    }
  }, [ref]);

  useEffect(() => {
    if (!selectedPlace) return;

    if (journeySelectPoint && editingJourney) {
      if (journeySelectPoint === JOURNEY_START_POINT) {
        setEditingJourney((prev) => ({
          ...prev!,
          startPoint: selectedPlace.point,
          originAddress: selectedPlace.address,
        }));
      } else if (journeySelectPoint === JOURNEY_END_POINT) {
        setEditingJourney((prev) => ({
          ...prev!,
          endPoint: selectedPlace.point,
          destinationAddress: selectedPlace.address,
        }));
      }
      resetJourneySelectPoint();
    } else {
      zoomTo(selectedPlace.point);
    }
  }, [selectedPlace, zoomTo]);

  return (
    <>
      <span
        className={css({
          position: 'absolute',
          top: theme.sizing.scale500,
          right: theme.sizing.scale500,
          backgroundColor: theme.colors.backgroundPrimary,
          boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
          borderRadius: '8px',
          minWidth: !isFocused ? '34px' : '250px',
          maxWidth: !isFocused ? '34px' : '250px',
          transition: '0.3s all ease-in-out',
          zIndex: isFocused ? 999 : 0,
        })}
        onClick={() => {
          if (ref.current) {
            ref.current.focus();
          }
        }}
      >
        <Input
          startEnhancer={
            <MaterialIcon
              name="place"
              size="mini"
            />
          }
          name={'map-search'}
          placeholder={'Buscar lugar'}
          size={'compact'}
          autoComplete="off"
          overrides={{
            StartEnhancer: {
              style: {
                paddingRight: theme.sizing.scale200,
                paddingLeft: theme.sizing.scale200,
                backgroundColor: 'transparent',
                position: 'absolute',
                top: '50%',
                left: 0,
                transform: 'translateY(-50%)',
              },
            },
            InputContainer: {
              style: {
                backgroundColor: theme.colors.backgroundPrimary,
              },
            },
            Input: {
              style: {
                backgroundColor: theme.colors.backgroundPrimary,
                paddingLeft: theme.sizing.scale850,
              },
              props: {
                ref,
              },
            },
            Root: {
              style: {
                paddingLeft: 0,
              },
            },
          }}
        />
      </span>
    </>
  );
}
